function normalizeAnswersList(value) {
	if (Array.isArray(value)) {
		return value
			.map((entry) =>
				String(entry ?? "")
					.trim()
					.toUpperCase(),
			)
			.filter(Boolean)
			.filter((entry, index, arr) => arr.indexOf(entry) === index);
	}

	if (value == null) return [];

	return String(value)
		.split(",")
		.map((entry) => entry.trim().toUpperCase())
		.filter(Boolean)
		.filter((entry, index, arr) => arr.indexOf(entry) === index);
}

function sortEntries(entries = []) {
	return [...entries].sort((a, b) => Number(a?.item_no || 0) - Number(b?.item_no || 0));
}

function normalizeSummary(summary = {}) {
	return {
		changed_answers: sortEntries(summary?.changed_answers || []).map((entry) => ({
			item_no: Number(entry?.item_no),
			original_answers: normalizeAnswersList(entry?.original_answers),
			modified_answers: normalizeAnswersList(entry?.modified_answers),
		})),
		alternate_keyings: sortEntries(summary?.alternate_keyings || []).map((entry) => ({
			item_no: Number(entry?.item_no),
			original_answers: normalizeAnswersList(entry?.original_answers),
			modified_answers: normalizeAnswersList(entry?.modified_answers),
		})),
		omitted_questions: [
			...new Set((summary?.omitted_questions || []).map((entry) => Number(entry))),
		]
			.filter((entry) => Number.isFinite(entry))
			.sort((a, b) => a - b),
	};
}

function buildSummaryFromItems(items = []) {
	const changedAnswers = [];
	const alternateKeyings = [];
	const omittedQuestions = [];

	for (const item of items || []) {
		const itemNo = Number(item?.item_no);
		if (!Number.isFinite(itemNo)) continue;

		const originalAnswers = normalizeAnswersList(item?.original_answer);
		const modifiedAnswers = normalizeAnswersList(item?.modified_answer);
		const isOmitted =
			String(item?.omit || "N")
				.trim()
				.toUpperCase() === "Y";

		if (isOmitted) {
			if (originalAnswers.length > 0 || modifiedAnswers.length > 0) {
				omittedQuestions.push(itemNo);
			}
			continue;
		}

		if (modifiedAnswers.length === 0) continue;
		if (
			modifiedAnswers.length === originalAnswers.length &&
			modifiedAnswers.every((answer, index) => answer === originalAnswers[index])
		) {
			continue;
		}

		const entry = {
			item_no: itemNo,
			original_answers: originalAnswers,
			modified_answers: modifiedAnswers,
		};

		if (modifiedAnswers.length > 1) {
			alternateKeyings.push(entry);
		} else {
			changedAnswers.push(entry);
		}
	}

	return normalizeSummary({
		changed_answers: changedAnswers,
		alternate_keyings: alternateKeyings,
		omitted_questions: omittedQuestions,
	});
}

function formatAnswers(answers = []) {
	const normalized = normalizeAnswersList(answers);
	return normalized.length > 0 ? normalized.join(",") : "(blank)";
}

export function getAnalysisKeyModificationSummary(analysis) {
	const summary = normalizeSummary(analysis?.key_modification_summary || {});
	if (
		summary.changed_answers.length > 0 ||
		summary.alternate_keyings.length > 0 ||
		summary.omitted_questions.length > 0
	) {
		return summary;
	}

	return buildSummaryFromItems(analysis?.answer_keys?.[0]?.items || []);
}

export function getKeyModificationSummaryLines(summary) {
	const normalized = normalizeSummary(summary);
	const lines = [];

	for (const entry of normalized.changed_answers) {
		lines.push(
			`Item ${entry.item_no} had answer ${formatAnswers(entry.original_answers)} but was modified to ${formatAnswers(entry.modified_answers)}`,
		);
	}

	for (const entry of normalized.alternate_keyings) {
		lines.push(
			`Item ${entry.item_no} has alternate keyings: Original ${formatAnswers(entry.original_answers)}, Modified ${formatAnswers(entry.modified_answers)}`,
		);
	}

	if (normalized.omitted_questions.length > 0) {
		lines.push(`Omitted from Scoring: ${normalized.omitted_questions.join(", ")}`);
	}

	return lines;
}

export function renderKeyModificationSummary(doc, summary, options = {}) {
	const lines = getKeyModificationSummaryLines(summary);
	if (lines.length === 0) {
		return options?.startY ?? 0;
	}

	const marginLeft = options?.marginLeft ?? 40;
	const marginRight = options?.marginRight ?? 40;
	const lineHeight = options?.lineHeight ?? 12;
	const gapAfter = options?.gapAfter ?? lineHeight / 2;
	const maxWidth = Number(doc?.internal?.pageSize?.getWidth?.() || 612) - marginLeft - marginRight;

	let y = options?.startY ?? 0;
	doc.setFont(undefined, "bold");
	for (const line of lines) {
		const wrapped = doc.splitTextToSize(line, maxWidth);
		for (const chunk of wrapped) {
			doc.text(chunk, marginLeft, y);
			y += lineHeight;
		}
	}
	doc.setFont(undefined, "normal");

	return y + gapAfter;
}
